import { useEffect } from 'react';
import getConfig from 'next/config';
import logger from '@finn-no/fiaas-logger';
import { trackPageView } from '@finn-no/pulse-sdk';
import { Box, Breadcrumbs } from '@fabric-ds/react';
import { fetchPodlets } from '@/server/podlets';

import { SimpleHead } from 'shared/page/Head';
import { Main } from 'shared/page/Main';
import { Menu } from 'shared/page/Menu';

import LandingScene from '@/scenes/landing/Landing';

import type { GetServerSideProps, NextPage } from 'next';
import type { CurrencyData } from '../interfaces';
import { runAllMiddlewares } from '@/server/middleware';

type CurrencyProps = { creditCardPodletContent: string; currencies: CurrencyData };

const {
    publicRuntimeConfig: { broadcastUrl },
    serverRuntimeConfig: { currencyApiUrl },
} = getConfig();

const CurrencyPage: NextPage<CurrencyProps> = ({ creditCardPodletContent, currencies }) => {
    useEffect(() => {
        // Pulse tracker id: TR_pv06 (see tracking section in README.md)
        trackPageView({
            object: {
                url: 'https://www.finn.no/reise/valuta/',
                type: 'Page',
                pageType: 'tool',
            },
            vertical: {
                name: 'travel',
            },
        });
    }, []);

    const description =
        'Bruk vår enkle valutakalkulator til å undersøke valutakursen før du reiser.' +
        'Sjekk kursen på dollar, pund, euro, baht og mange andre valutaer.';

    return (
        <>
            <SimpleHead
                title="Hva er valutakursen? | Valutakalkulator | FINN reise"
                canonical="https://www.finn.no/reise/valuta/"
                description={description}
            />

            <Main broadcastUrl={broadcastUrl}>
                <Breadcrumbs className="px-0 sm:px-16 pt-16">
                    <a href="/reise/">Reise</a>
                    <a href="/reise/valuta/">Valuta</a>
                </Breadcrumbs>
                <Menu />
                <Box bleed>
                    <LandingScene currencies={currencies} creditCardPodletContent={creditCardPodletContent} />
                </Box>
            </Main>
        </>
    );
};

export const getServerSideProps: GetServerSideProps<CurrencyProps> = async ({ req, res, query }) => {
    await runAllMiddlewares(req, res, query);

    const creditCardPodletContent = await fetchPodlets.contentForSingle('creditCard', req, res);

    const currencies = await fetch(currencyApiUrl)
        .then((cur) => cur.json())
        .catch((err) => {
            logger.error(err);
            return {};
        });

    return { props: { creditCardPodletContent, currencies } };
};

export default CurrencyPage;
